/**
 * confirm-dialog — переиспользуемое модальное подтверждение (нативный `<dialog>`).
 *
 * Generic «да/нет»-вопрос с заголовком, текстом и двумя кнопками. Возвращает
 * `Promise<boolean>` (true = подтвердил, false = отменил / Esc / клик по фону).
 * Нативный `showModal()` даёт top-layer, `::backdrop`, focus-trap, Esc из коробки.
 *
 * Применяется leave-guard'ом (предупреждение перед выходом из запущенной игры) и
 * любым будущим местом, где нужно подтверждение. Все размеры — токенами
 * (--m / --fs-control / --fs-body / --r), magic-px нет.
 */

import './dialog-plaque.css';
import './confirm-dialog.css';

export interface ConfirmDialogOptions {
  readonly title: string;
  readonly message?: string;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
}

export function confirmDialog(opts: ConfirmDialogOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const dialog = document.createElement('dialog');
    // dialog-plaque — общий примитив-контейнер плашки (вид/ширина/тень); confirm-dialog —
    // только внутренняя раскладка (заголовок/текст/кнопки).
    dialog.className = 'dialog-plaque confirm-dialog';
    dialog.setAttribute('role', 'alertdialog');

    // Акцентная (оранжевая) кнопка — «Остаться» (безопасный выбор), action-кнопка
    // «Завершить» — обычная (owner-directive 2026-05-30: эмфаза на безопасном).
    dialog.innerHTML = `
      <h2 class="confirm-dialog-title"></h2>
      ${opts.message ? '<p class="confirm-dialog-message"></p>' : ''}
      <div class="confirm-dialog-actions">
        <button type="button" class="btn primary confirm-dialog-cancel"></button>
        <button type="button" class="btn confirm-dialog-confirm"></button>
      </div>
    `;
    dialog.querySelector<HTMLElement>('.confirm-dialog-title')!.textContent = opts.title;
    if (opts.message) {
      dialog.querySelector<HTMLElement>('.confirm-dialog-message')!.textContent = opts.message;
    }
    const cancelBtn = dialog.querySelector<HTMLButtonElement>('.confirm-dialog-cancel')!;
    const confirmBtn = dialog.querySelector<HTMLButtonElement>('.confirm-dialog-confirm')!;
    cancelBtn.textContent = opts.cancelLabel;
    confirmBtn.textContent = opts.confirmLabel;

    document.body.appendChild(dialog);

    let settled = false;
    const finish = (result: boolean): void => {
      if (settled) return;
      settled = true;
      resolve(result);
      if (dialog.open) dialog.close();
    };

    cancelBtn.addEventListener('click', () => finish(false));
    confirmBtn.addEventListener('click', () => finish(true));
    // Esc — нативно стреляет `cancel`; трактуем как отмену.
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      finish(false);
    });
    // Клик по ::backdrop (target === dialog) — отмена.
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) finish(false);
    });
    // После закрытия — снять из DOM.
    dialog.addEventListener('close', () => {
      finish(false); // на случай программного close без явного выбора
      dialog.remove();
    });

    dialog.showModal();
    // Фокус по умолчанию — на безопасной кнопке (Отмена/Остаться).
    cancelBtn.focus();
  });
}
